import { useState } from "react";
import DeleteAccountModal from "@components/Nav/DeleteAccountModal";
import deleteAccount, { ACCOUNT_DELETE_STATUS } from "@api/deleteAccount";
import { HOME_BASE_URL } from "@config";
import { navigateTo } from "@utils/navigate";
import useSegment from "@hooks/useSegment";
import { ANALYTICS_EVENTS, ANALYTICS_LOCATIONS } from "@appTypes/analytics";

export type DeleteAccountModalState = "confirm" | "success" | "failure" | "coachMatchFailure";

const useHandleDeleteAccount = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalState, setModalState] = useState<DeleteAccountModalState>("confirm");
  const { trackEvent } = useSegment();

  const handleOpen = () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Delete Your TrainingPeaks Account button clicked",
      ANALYTICS_LOCATIONS.SIDE_NAVIGATION
    );
    setIsOpen(true);
  };

  const handleClose = () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Delete Account Modal - Cancel button clicked",
      ANALYTICS_LOCATIONS.SIDE_NAVIGATION
    );
    setIsOpen(false);
    setModalState("confirm");
    setIsProcessing(false);
  };

  const handleSuccessClose = () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Delete Account Modal - Success Close button clicked",
      ANALYTICS_LOCATIONS.SIDE_NAVIGATION
    );
    navigateTo(`${HOME_BASE_URL}/logout`);
  };

  const handleXClose = () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Delete Account Modal - X button clicked",
      ANALYTICS_LOCATIONS.SIDE_NAVIGATION
    );
    if (modalState === "success") {
      navigateTo(`${HOME_BASE_URL}/logout`);
    } else {
      setIsOpen(false);
      setModalState("confirm");
      setIsProcessing(false);
    }
  };

  const handleConfirm = async () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Delete Account Modal - Proceed button clicked",
      ANALYTICS_LOCATIONS.SIDE_NAVIGATION
    );
    setIsProcessing(true);
    const response = await deleteAccount();
    setIsProcessing(false);

    if (!response.success || response.data === null) {
      setModalState("failure");
      return;
    }

    if (response.data.value === ACCOUNT_DELETE_STATUS.SUCCESS) {
      setModalState("success");
    } else if (response.data.value === ACCOUNT_DELETE_STATUS.FAILURE_COACH_MATCH) {
      setModalState("coachMatchFailure");
    } else {
      setModalState("failure");
    }
  };

  const modal = (
    <DeleteAccountModal
      open={isOpen}
      modalState={modalState}
      isProcessing={isProcessing}
      onClose={handleClose}
      onXClose={handleXClose}
      onConfirm={handleConfirm}
      onSuccessClose={handleSuccessClose}
    />
  );

  return { handleDeleteAccountOpen: handleOpen, modal };
};

export default useHandleDeleteAccount;
